import React, { useState, useEffect } from 'react';
import { SelectField } from "components/ui/select-field";
import {
  AIAgent,
  AgentPerformance,
  PerformanceDataPoint,
  AgentFeedback,
  AgentAlert
} from '../../../types/aiAgent';

interface AIAgentPerformanceMonitorProps {
  agent: AIAgent;
  onAlertAction?: (alertId: string, action: 'acknowledge' | 'resolve') => void;
}

export const AIAgentPerformanceMonitor: React.FC<AIAgentPerformanceMonitorProps> = ({
  agent,
  onAlertAction
}) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'metrics' | 'trends' | 'feedback' | 'alerts'>('overview');
  const [timeRange, setTimeRange] = useState<'24h' | '7d' | '30d' | '90d'>('7d');
  const [performanceData, setPerformanceData] = useState<PerformanceDataPoint[]>([]);
  const [alerts, setAlerts] = useState<AgentAlert[]>([]);
  const [feedback, setFeedback] = useState<AgentFeedback[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadPerformanceData();
  }, [agent.id, timeRange]);

  const loadPerformanceData = async () => {
    try {
      setLoading(true);
      // Mock implementation - would load from service
      const mockData: PerformanceDataPoint[] = generateMockPerformanceData(timeRange);
      const mockAlerts: AgentAlert[] = generateMockAlerts();
      const mockFeedback: AgentFeedback[] = generateMockFeedback();
      
      setPerformanceData(mockData);
      setAlerts(mockAlerts);
      setFeedback(mockFeedback);
    } catch (error) {
      console.error('Failed to load performance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateMockPerformanceData = (range: string): PerformanceDataPoint[] => {
    const days = range === '24h' ? 1 : range === '7d' ? 7 : range === '30d' ? 30 : 90;
    const points: PerformanceDataPoint[] = [];
    
    for (let i = days; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      
      points.push({
        timestamp: date,
        successRate: 0.75 + Math.random() * 0.2,
        confidence: 0.8 + Math.random() * 0.15,
        tasksCompleted: Math.floor(Math.random() * 10) + 1,
        improvementGenerated: Math.random() * 0.3,
        userSatisfaction: 3.5 + Math.random() * 1.5
      });
    }
    
    return points;
  };

  const generateMockAlerts = (): AgentAlert[] => [
    {
      id: 'alert_1',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000),
      severity: 'warning',
      type: 'performance_degradation',
      message: 'Success rate has dropped below target threshold',
      context: { threshold: 0.8, current: 0.72 },
      resolved: false
    },
    {
      id: 'alert_2',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000),
      severity: 'info',
      type: 'milestone_achieved',
      message: 'Agent has completed 100 successful tasks',
      context: { milestone: 100 },
      resolved: true,
      resolvedAt: new Date(Date.now() - 5 * 60 * 60 * 1000),
      resolution: 'Milestone acknowledged'
    }
  ];

  const generateMockFeedback = (): AgentFeedback[] => [
    {
      id: 'feedback_1',
      fromUserId: 'user_1',
      timestamp: new Date(Date.now() - 24 * 60 * 60 * 1000),
      type: 'positive',
      category: 'performance',
      rating: 4,
      comment: 'Agent performed well on the recent A/B test optimization',
      context: { taskId: 'task_123' }
    },
    {
      id: 'feedback_2',
      fromUserId: 'user_2',
      timestamp: new Date(Date.now() - 48 * 60 * 60 * 1000),
      type: 'suggestion',
      category: 'decision_quality',
      rating: 3,
      comment: 'Could improve statistical analysis explanations',
      context: { decisionId: 'decision_456' }
    }
  ];

  const getPerformanceStatus = (performance: AgentPerformance) => {
    const { successRate, improvementRate } = performance.overall;
    
    // Provide default values if performanceTargets is undefined
    const performanceTargets = agent.configuration.performanceTargets || {
      successRate: 0.8,
      improvementRate: 0.1,
      responseTime: 30
    };
    
    const { successRate: targetSuccess, improvementRate: targetImprovement } = performanceTargets;
    
    if (successRate >= targetSuccess && improvementRate >= targetImprovement) {
      return { status: 'excellent', color: 'text-green-600 dark:text-green-300', bg: 'bg-green-100 dark:bg-green-900/30' };
    } else if (successRate >= targetSuccess * 0.9 && improvementRate >= targetImprovement * 0.9) {
      return { status: 'good', color: 'text-blue-600 dark:text-blue-300', bg: 'bg-blue-100 dark:bg-blue-900/30' };
    } else if (successRate >= targetSuccess * 0.8 && improvementRate >= targetImprovement * 0.8) {
      return { status: 'fair', color: 'text-yellow-600 dark:text-yellow-300', bg: 'bg-yellow-100 dark:bg-yellow-900/30' };
    } else {
      return { status: 'needs improvement', color: 'text-red-600 dark:text-red-300', bg: 'bg-red-100 dark:bg-red-900/30' };
    }
  };

  const _getAlertSeverityColor = (severity: AgentAlert['severity']) => {
    const colors = {
      info: 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30',
      warning: 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30',
      error: 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
      critical: 'text-red-800 dark:text-red-300 bg-red-200 dark:bg-red-900/40'
    };
    return colors[severity];
  };

  const performanceStatus = getPerformanceStatus(agent.performance);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-surface rounded-lg shadow-sm border border-line">
      {/* Header */}
      <div className="border-b border-line px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-content">
              Performance Monitor - {agent.name}
            </h3>
            <div className="flex items-center space-x-4 mt-2">
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${performanceStatus.bg} ${performanceStatus.color}`}>
                {performanceStatus.status.toUpperCase()}
              </span>
              <span className="text-sm text-content-secondary">
                Last updated: {new Date().toLocaleTimeString()}
              </span>
            </div>
          </div>
          
          <div className="flex items-center space-x-3">
            <SelectField
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value as any)}
              className="border border-line-strong rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="24h">Last 24 Hours</option>
              <option value="7d">Last 7 Days</option>
              <option value="30d">Last 30 Days</option>
              <option value="90d">Last 90 Days</option>
            </SelectField>
          </div>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="border-b border-line">
        <nav className="flex space-x-8 px-6">
          {[
            { id: 'overview', label: 'Overview', icon: '📊' },
            { id: 'metrics', label: 'Detailed Metrics', icon: '📈' },
            { id: 'trends', label: 'Trends', icon: '📉' },
            { id: 'feedback', label: 'Feedback', icon: '💬' },
            { id: 'alerts', label: 'Alerts', icon: '🚨', badge: alerts.filter(a => !a.resolved).length }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'border-blue-500 text-blue-600 dark:text-blue-300'
                  : 'border-transparent text-content-muted hover:text-gray-700 dark:hover:text-gray-300 hover:border-line-strong'
              }`}
            >
              <span>{tab.icon}</span>
              <span>{tab.label}</span>
              {tab.badge && tab.badge > 0 && (
                <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1 min-w-[20px] text-center">
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      <div className="p-6">
        {activeTab === 'overview' && (
          <PerformanceOverviewTab
            agent={agent}
            performanceData={performanceData}
            timeRange={timeRange}
          />
        )}

        {activeTab === 'metrics' && (
          <DetailedMetricsTab
            agent={agent}
            performanceData={performanceData}
          />
        )}

        {activeTab === 'trends' && (
          <TrendsTab
            performanceData={performanceData}
            timeRange={timeRange}
          />
        )}

        {activeTab === 'feedback' && (
          <FeedbackTab
            feedback={feedback}
            agent={agent}
          />
        )}

        {activeTab === 'alerts' && (
          <AlertsTab
            alerts={alerts}
            onAlertAction={onAlertAction}
          />
        )}
      </div>
    </div>
  );
};

// Performance Overview Tab
const PerformanceOverviewTab: React.FC<{
  agent: AIAgent;
  performanceData: PerformanceDataPoint[];
  timeRange: string;
}> = ({ agent, performanceData, timeRange: _timeRange }) => {
  const performance = agent.performance.overall;
  const targets = agent.configuration.performanceTargets || {
    successRate: 0.8,
    improvementRate: 0.1,
    responseTime: 30
  };
  
  const recentData = performanceData.slice(-7); // Last 7 data points
  const avgSuccessRate = recentData.reduce((sum, d) => sum + d.successRate, 0) / recentData.length;
  const avgConfidence = recentData.reduce((sum, d) => sum + d.confidence, 0) / recentData.length;
  const totalTasks = recentData.reduce((sum, d) => sum + d.tasksCompleted, 0);

  return (
    <div className="space-y-6">
      {/* Key Performance Indicators */}
      <div className="grid grid-cols-4 gap-6">
        <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Success Rate</h4>
          <div className="text-3xl font-bold">{(avgSuccessRate * 100).toFixed(1)}%</div>
          <div className="text-blue-100 text-sm mt-1">
            Target: {(targets.successRate * 100).toFixed(0)}%
          </div>
          <div className="mt-2">
            {avgSuccessRate >= targets.successRate ? (
              <span className="text-green-200 text-sm">✓ On Target</span>
            ) : (
              <span className="text-red-200 text-sm">⚠ Below Target</span>
            )}
          </div>
        </div>

        <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Confidence</h4>
          <div className="text-3xl font-bold">{(avgConfidence * 100).toFixed(1)}%</div>
          <div className="text-green-100 text-sm mt-1">
            Average decision confidence
          </div>
        </div>

        <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Tasks Completed</h4>
          <div className="text-3xl font-bold">{totalTasks}</div>
          <div className="text-purple-100 text-sm mt-1">
            In selected period
          </div>
        </div>

        <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-6 text-white">
          <h4 className="text-lg font-semibold mb-2">Improvement Rate</h4>
          <div className="text-3xl font-bold">{(performance.improvementRate * 100).toFixed(1)}%</div>
          <div className="text-orange-100 text-sm mt-1">
            Target: {(targets.improvementRate * 100).toFixed(1)}%
          </div>
          <div className="mt-2">
            {performance.improvementRate >= targets.improvementRate ? (
              <span className="text-green-200 text-sm">✓ On Target</span>
            ) : (
              <span className="text-red-200 text-sm">⚠ Below Target</span>
            )}
          </div>
        </div>
      </div>

      {/* Performance by Task Type */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Performance by Task Type</h4>
        <div className="grid grid-cols-2 gap-4">
          {Object.entries(agent.performance.byTaskType).map(([taskType, perf]) => (
            <div key={taskType} className="border border-line rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h5 className="font-medium text-content capitalize">
                  {taskType.replace('_', ' ')}
                </h5>
                <span className="text-sm text-content-secondary">{perf.count} tasks</span>
              </div>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-content-secondary">Success Rate</span>
                  <span className="font-medium">{(perf.successRate * 100).toFixed(1)}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full"
                    style={{ width: `${perf.successRate * 100}%` }}
                  ></div>
                </div>
                
                <div className="flex items-center justify-between text-sm">
                  <span className="text-content-secondary">Avg Confidence</span>
                  <span className="font-medium">{(perf.averageConfidence * 100).toFixed(1)}%</span>
                </div>
                
                {perf.lastPerformed && (
                  <div className="text-xs text-content-muted">
                    Last: {perf.lastPerformed.toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Benchmarks */}
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Performance Benchmarks</h4>
        <div className="grid grid-cols-3 gap-4">
          <div className="border border-line rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-content">
              {agent.performance.benchmarks.vsOtherAgents}th
            </div>
            <div className="text-sm text-content-secondary">Percentile vs Other Agents</div>
          </div>
          
          <div className="border border-line rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-content">
              {agent.performance.benchmarks.vsHistorical > 0 ? '+' : ''}{(agent.performance.benchmarks.vsHistorical * 100).toFixed(1)}%
            </div>
            <div className="text-sm text-content-secondary">vs Historical Performance</div>
          </div>
          
          <div className="border border-line rounded-lg p-4 text-center">
            <div className="text-2xl font-bold text-content">
              {Object.keys(agent.performance.benchmarks.vsTargets).length}
            </div>
            <div className="text-sm text-content-secondary">Targets Met</div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Detailed Metrics Tab
const DetailedMetricsTab: React.FC<{
  agent: AIAgent;
  performanceData: PerformanceDataPoint[];
}> = ({ agent, performanceData: _performanceData }) => {
  const targets = agent.configuration.performanceTargets || {
    successRate: 0.8,
    improvementRate: 0.1,
    responseTime: 30
  };

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Detailed Performance Metrics</h4>
        
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50 dark:bg-dark-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                  Metric
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                  Current Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                  Target
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-content-muted uppercase tracking-wider">
                  Trend
                </th>
              </tr>
            </thead>
            <tbody className="bg-surface divide-y divide-gray-200">
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                  Overall Success Rate
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {(agent.performance.overall.successRate * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {(targets.successRate * 100).toFixed(0)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {agent.performance.overall.successRate >= targets.successRate ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                      On Target
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full">
                      Below Target
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  ↗ Improving
                </td>
              </tr>
              
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                  Average Confidence
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {(agent.performance.overall.averageConfidence * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  85%
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300 rounded-full">
                    Good
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  → Stable
                </td>
              </tr>
              
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                  Tasks Completed
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {agent.performance.overall.tasksCompleted}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  -
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className="px-2 py-1 text-xs font-medium bg-gray-100 dark:bg-dark-700 text-gray-800 dark:text-gray-200 rounded-full">
                    N/A
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  ↗ Increasing
                </td>
              </tr>
              
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                  Improvement Rate
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {(agent.performance.overall.improvementRate * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {(targets.improvementRate * 100).toFixed(1)}%
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {agent.performance.overall.improvementRate >= targets.improvementRate ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                      On Target
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
                      Needs Improvement
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  ↗ Improving
                </td>
              </tr>
              
              <tr>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-content">
                  Average Task Duration
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {agent.performance.overall.averageTaskDuration.toFixed(0)}s
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  {targets.responseTime}s
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  {agent.performance.overall.averageTaskDuration <= targets.responseTime ? (
                    <span className="px-2 py-1 text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 rounded-full">
                      On Target
                    </span>
                  ) : (
                    <span className="px-2 py-1 text-xs font-medium bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300 rounded-full">
                      Too Slow
                    </span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-content">
                  ↘ Decreasing
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// Trends Tab
const TrendsTab: React.FC<{
  performanceData: PerformanceDataPoint[];
  timeRange: string;
}> = ({ performanceData, timeRange: _timeRange }) => {
  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">Performance Trends</h4>
        <p className="text-sm text-content-secondary mb-6">
          Performance metrics over the selected time period
        </p>
      </div>

      {/* Simple trend visualization */}
      <div className="grid grid-cols-2 gap-6">
        <div className="border border-line rounded-lg p-4">
          <h5 className="font-medium text-content mb-3">Success Rate Trend</h5>
          <div className="space-y-2">
            {performanceData.slice(-10).map((point, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-content-secondary">
                  {point.timestamp.toLocaleDateString()}
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full"
                      style={{ width: `${point.successRate * 100}%` }}
                    ></div>
                  </div>
                  <span className="font-medium w-12">
                    {(point.successRate * 100).toFixed(0)}%
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-line rounded-lg p-4">
          <h5 className="font-medium text-content mb-3">Tasks Completed</h5>
          <div className="space-y-2">
            {performanceData.slice(-10).map((point, index) => (
              <div key={index} className="flex items-center justify-between text-sm">
                <span className="text-content-secondary">
                  {point.timestamp.toLocaleDateString()}
                </span>
                <div className="flex items-center space-x-2">
                  <div className="w-20 bg-gray-200 dark:bg-dark-600 rounded-full h-2">
                    <div
                      className="bg-green-600 h-2 rounded-full"
                      style={{ width: `${Math.min(point.tasksCompleted * 10, 100)}%` }}
                    ></div>
                  </div>
                  <span className="font-medium w-12">
                    {point.tasksCompleted}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

// Feedback Tab
const FeedbackTab: React.FC<{
  feedback: AgentFeedback[];
  agent: AIAgent;
}> = ({ feedback, agent: _agent }) => {
  const getFeedbackTypeColor = (type: AgentFeedback['type']) => {
    const colors = {
      positive: 'text-green-600 dark:text-green-300 bg-green-100 dark:bg-green-900/30',
      negative: 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30',
      neutral: 'text-content-secondary bg-surface-sunken',
      suggestion: 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30'
    };
    return colors[type];
  };

  const averageRating = feedback.length > 0 
    ? feedback.reduce((sum, f) => sum + f.rating, 0) / feedback.length 
    : 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-lg font-semibold text-content">User Feedback</h4>
          <p className="text-sm text-content-secondary">
            Feedback from users about agent performance
          </p>
        </div>
        <div className="text-right">
          <div className="text-2xl font-bold text-content">
            {averageRating.toFixed(1)}/5
          </div>
          <div className="text-sm text-content-secondary">Average Rating</div>
        </div>
      </div>

      <div className="space-y-4">
        {feedback.map(item => (
          <div key={item.id} className="border border-line rounded-lg p-4">
            <div className="flex items-start justify-between mb-2">
              <div className="flex items-center space-x-3">
                <span className={`px-2 py-1 rounded-full text-xs font-medium ${getFeedbackTypeColor(item.type)}`}>
                  {item.type}
                </span>
                <span className="text-sm text-content-secondary capitalize">
                  {item.category.replace('_', ' ')}
                </span>
                <div className="flex items-center">
                  {[1, 2, 3, 4, 5].map(star => (
                    <svg
                      key={star}
                      className={`w-4 h-4 ${star <= item.rating ? 'text-yellow-400' : 'text-gray-300'}`}
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                    </svg>
                  ))}
                </div>
              </div>
              <span className="text-sm text-content-muted">
                {item.timestamp.toLocaleDateString()}
              </span>
            </div>
            
            <p className="text-content mb-2">{item.comment}</p>
            
            {item.agentResponse && (
              <div className="bg-blue-50 dark:bg-blue-900/30 rounded-lg p-3 mt-3">
                <div className="text-sm font-medium text-blue-900 dark:text-blue-300 mb-1">
                  Agent Response
                </div>
                <div className="text-sm text-blue-800 dark:text-blue-300">
                  Learning applied: {item.agentResponse.learningApplied.join(', ')}
                </div>
              </div>
            )}
          </div>
        ))}
        
        {feedback.length === 0 && (
          <div className="text-center py-8">
            <div className="text-content-subtle text-4xl mb-2">💬</div>
            <h5 className="text-lg font-medium text-content mb-1">No feedback yet</h5>
            <p className="text-content-secondary">User feedback will appear here</p>
          </div>
        )}
      </div>
    </div>
  );
};

// Alerts Tab
const AlertsTab: React.FC<{
  alerts: AgentAlert[];
  onAlertAction?: (alertId: string, action: 'acknowledge' | 'resolve') => void;
}> = ({ alerts, onAlertAction }) => {
  const getAlertSeverityColor = (severity: AgentAlert['severity']) => {
    const colors = {
      info: 'text-blue-600 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/50',
      warning: 'text-yellow-600 dark:text-yellow-300 bg-yellow-100 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800/50',
      error: 'text-red-600 dark:text-red-300 bg-red-100 dark:bg-red-900/30 border-red-200 dark:border-red-800/50',
      critical: 'text-red-800 dark:text-red-300 bg-red-200 dark:bg-red-900/40 border-red-300 dark:border-red-800/50'
    };
    return colors[severity];
  };

  const getAlertIcon = (severity: AgentAlert['severity']) => {
    const icons = {
      info: 'ℹ️',
      warning: '⚠️',
      error: '❌',
      critical: '🚨'
    };
    return icons[severity];
  };

  const activeAlerts = alerts.filter(alert => !alert.resolved);
  const resolvedAlerts = alerts.filter(alert => alert.resolved);

  return (
    <div className="space-y-6">
      <div>
        <h4 className="text-lg font-semibold text-content mb-4">
          Active Alerts ({activeAlerts.length})
        </h4>
        
        <div className="space-y-3">
          {activeAlerts.map(alert => (
            <div
              key={alert.id}
              className={`border rounded-lg p-4 ${getAlertSeverityColor(alert.severity)}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-3">
                  <span className="text-xl">{getAlertIcon(alert.severity)}</span>
                  <div>
                    <div className="font-medium text-content mb-1">
                      {alert.message}
                    </div>
                    <div className="text-sm text-content-secondary mb-2">
                      {alert.timestamp.toLocaleString()}
                    </div>
                    {alert.context && (
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <strong>Context:</strong> {JSON.stringify(alert.context)}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className="flex space-x-2">
                  <button
                    onClick={() => onAlertAction?.(alert.id, 'acknowledge')}
                    className="px-3 py-1 text-sm bg-surface border border-line-strong rounded hover:bg-gray-50 dark:hover:bg-dark-700"
                  >
                    Acknowledge
                  </button>
                  <button
                    onClick={() => onAlertAction?.(alert.id, 'resolve')}
                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                  >
                    Resolve
                  </button>
                </div>
              </div>
            </div>
          ))}
          
          {activeAlerts.length === 0 && (
            <div className="text-center py-8">
              <div className="text-content-subtle text-4xl mb-2">✅</div>
              <h5 className="text-lg font-medium text-content mb-1">No active alerts</h5>
              <p className="text-content-secondary">All systems operating normally</p>
            </div>
          )}
        </div>
      </div>

      {resolvedAlerts.length > 0 && (
        <div>
          <h4 className="text-lg font-semibold text-content mb-4">
            Recently Resolved ({resolvedAlerts.length})
          </h4>
          
          <div className="space-y-3">
            {resolvedAlerts.slice(0, 5).map(alert => (
              <div
                key={alert.id}
                className="border border-line rounded-lg p-4 bg-gray-50 dark:bg-dark-700"
              >
                <div className="flex items-start space-x-3">
                  <span className="text-xl opacity-50">{getAlertIcon(alert.severity)}</span>
                  <div className="flex-1">
                    <div className="font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {alert.message}
                    </div>
                    <div className="text-sm text-content-muted mb-1">
                      Resolved: {alert.resolvedAt?.toLocaleString()}
                    </div>
                    {alert.resolution && (
                      <div className="text-sm text-content-secondary">
                        <strong>Resolution:</strong> {alert.resolution}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};